import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { 
  trackPageView, 
  trackEvent, 
  trackLawsuitEvent, 
  trackUserInteraction, 
  trackFormSubmission, 
  trackSearch, 
  trackPayment, 
  setUserProperties,
  trackPerformance,
  trackError,
  AnalyticsEvents 
} from '../utils/analytics'

export const useAnalytics = () => {
  const location = useLocation()

  // Track page views automatically
  useEffect(() => {
    trackPageView(location.pathname + location.search)
  }, [location])

  return {
    // Basic tracking functions
    trackEvent,
    trackPageView,
    
    // Specific event trackers
    trackLawsuitEvent,
    trackUserInteraction,
    trackFormSubmission,
    trackSearch,
    trackPayment,
    setUserProperties,
    trackPerformance,
    trackError,
    
    // Pre-defined events
    events: AnalyticsEvents,
    
    // Convenience methods for common legal platform actions
    trackAuth: {
      signUp: (method?: string) => trackEvent(AnalyticsEvents.SIGN_UP, { method }),
      login: (method?: string) => trackEvent(AnalyticsEvents.LOGIN, { method }),
      logout: () => trackEvent(AnalyticsEvents.LOGOUT),
    },
    
    trackLawsuit: {
      startFiling: (lawsuitType?: string, country?: string) => 
        trackLawsuitEvent(AnalyticsEvents.START_LAWSUIT_FILING, { lawsuit_type: lawsuitType, country }),
      completeFiling: (lawsuitId: string, lawsuitType?: string, amount?: number) => 
        trackLawsuitEvent(AnalyticsEvents.COMPLETE_LAWSUIT_FILING, { lawsuit_id: lawsuitId, lawsuit_type: lawsuitType, amount }),
      join: (lawsuitId: string, lawsuitType?: string) => 
        trackLawsuitEvent(AnalyticsEvents.JOIN_LAWSUIT, { lawsuit_id: lawsuitId, lawsuit_type: lawsuitType }),
      browse: (filters?: Record<string, any>) => 
        trackEvent(AnalyticsEvents.BROWSE_LAWSUITS, { filters }),
      viewDetails: (lawsuitId: string, lawsuitType?: string) => 
        trackLawsuitEvent(AnalyticsEvents.VIEW_LAWSUIT_DETAILS, { lawsuit_id: lawsuitId, lawsuit_type: lawsuitType }),
      strengthenCase: (lawsuitId: string, evidenceType?: string) => 
        trackEvent(AnalyticsEvents.STRENGTHEN_CASE, { lawsuit_id: lawsuitId, evidence_type: evidenceType }),
    },
    
    trackDocuments: {
      generate: (documentType: string, lawsuitId?: string) => 
        trackEvent(AnalyticsEvents.GENERATE_DOCUMENTS, { document_type: documentType, lawsuit_id: lawsuitId }),
      download: (documentType: string, lawsuitId?: string) => 
        trackEvent(AnalyticsEvents.DOWNLOAD_DOCUMENT, { document_type: documentType, lawsuit_id: lawsuitId }),
      template: (templateType: string) => 
        trackEvent(AnalyticsEvents.DOWNLOAD_LEGAL_TEMPLATE, { template_type: templateType }),
    },
    
    trackAI: {
      consultationStart: (consultationType?: string) => 
        trackEvent(AnalyticsEvents.AI_CONSULTATION_START, { consultation_type: consultationType }),
      consultationComplete: (consultationType?: string, duration?: number) => 
        trackEvent(AnalyticsEvents.AI_CONSULTATION_COMPLETE, { consultation_type: consultationType, duration }),
      documentGeneration: (documentType: string, inputLength?: number) => 
        trackEvent(AnalyticsEvents.AI_DOCUMENT_GENERATION, { document_type: documentType, input_length: inputLength }),
    },
    
    trackNavigation: {
      search: (searchTerm: string, category?: string, resultsCount?: number) => 
        trackSearch(searchTerm, category, resultsCount),
      share: (contentType: string, method?: string) => 
        trackEvent(AnalyticsEvents.SHARE, { content_type: contentType, method }),
      languageChange: (fromLanguage: string, toLanguage: string) => 
        trackEvent(AnalyticsEvents.LANGUAGE_CHANGE, { from_language: fromLanguage, to_language: toLanguage }),
      countrySelection: (country: string) => 
        trackEvent(AnalyticsEvents.COUNTRY_SELECTION, { country }),
    },
    
    trackEngagement: {
      contactForm: (success: boolean, inquiryType?: string) => 
        trackFormSubmission('contact_form', success, inquiryType),
      faqView: (question: string, category?: string) => 
        trackEvent(AnalyticsEvents.FAQ_VIEW, { question, category }),
      pricingView: (plan?: string) => 
        trackEvent(AnalyticsEvents.PRICING_VIEW, { plan }),
    },
    
    trackUserJourney: {
      onboardingStart: () => trackEvent(AnalyticsEvents.ONBOARDING_START),
      onboardingComplete: (duration?: number, stepsCompleted?: number) => 
        trackEvent(AnalyticsEvents.ONBOARDING_COMPLETE, { duration, steps_completed: stepsCompleted }),
      profileUpdate: (fieldsUpdated: string[]) => 
        trackEvent(AnalyticsEvents.PROFILE_UPDATE, { fields_updated: fieldsUpdated }),
    },
  }
}

// Hook for tracking component mount/unmount and performance
export const useComponentAnalytics = (componentName: string) => {
  useEffect(() => {
    const startTime = performance.now()
    
    // Track component mount
    trackEvent('component_mount', {
      event_category: 'performance',
      component_name: componentName,
    })
    
    return () => {
      // Track component unmount and time spent
      const timeSpent = performance.now() - startTime
      trackEvent('component_unmount', {
        event_category: 'performance',
        component_name: componentName,
        time_spent: Math.round(timeSpent),
      })
    }
  }, [componentName])
}

// Hook for tracking form analytics
export const useFormAnalytics = (formName: string) => {
  const trackFormStart = () => {
    trackEvent('form_start', {
      event_category: 'form',
      form_name: formName,
    })
  }
  
  const trackFormFieldInteraction = (fieldName: string, action: 'focus' | 'blur' | 'change') => {
    trackEvent('form_field_interaction', {
      event_category: 'form',
      form_name: formName,
      field_name: fieldName,
      action,
    })
  }
  
  const trackFormValidationError = (fieldName: string, errorMessage: string) => {
    trackEvent('form_validation_error', {
      event_category: 'form',
      form_name: formName,
      field_name: fieldName,
      error_message: errorMessage,
    })
  }
  
  const trackFormSubmit = (success: boolean, errorMessage?: string, formData?: Record<string, any>) => {
    trackFormSubmission(formName, success, errorMessage)
    
    if (success && formData) {
      // Track successful form submission with sanitized data
      trackEvent('form_submit_success_detailed', {
        event_category: 'form',
        form_name: formName,
        field_count: Object.keys(formData).length,
        // Don't include actual form data for privacy
      })
    }
  }
  
  return {
    trackFormStart,
    trackFormFieldInteraction,
    trackFormValidationError,
    trackFormSubmit,
  }
}